<template>
  <div id="app">
    <div
      v-if="
        !this.$route.path.match('callback|signin|logout|new-user-welcome-page')
      "
    >
      <b-notification
        v-if="announcementBannerVisible"
        class="dij-announcement-banner"
        has-icon
        :aria-close-label="$t('close_banner')"
        @close="hideAnnouncementBanner"
        :message="announcementBannerMessage"
      >
      </b-notification>
      <Header />
      <ProgressTaskBar
        :demo="false"
        :value="progressBarValue"
        :message="progressBarMessage"
      />
    </div>
    <div class="notification-area">
      <b-notification
        v-if="notificationVisible"
        :type="notificationType"
        :auto-close="notificationAutoClose"
        :duration="notificationAutoCloseDuration"
        :aria-close-label="$t('close_notification')"
        has-icon
        role="alert"
        @close="hideNotification"
      >
        {{ notificationMessage }}
      </b-notification>
    </div>

    <conflict-dialog
      :visible="conflictDialogVisible"
      :conflicts="dialogConflicts"
      @resolved="onResolved"
      @cancel="onCancel"
    />
    <router-view :key="$route.fullPath" />
  </div>
</template>

<script>
import Header from './components/layout/Header';
import ProgressTaskBar from './components/Shared/ProgressTaskBar';
import { acquireToken } from './services/auth';
import onlineService from './services/onlineService';

import OfflineMixin from './mixins/OfflineMixin';
import ConnectivitySyncMixin from './mixins/ConnectivitySyncMixin';
import ConflictDialog from './components/ConflictDialog';
import { GET_ALL_CONFLICTS } from './store/conflicts/getters/getterTypes';
import { CONFLICTS_CLEAR_ALL_CONFLICTS } from './store/conflicts/mutations/mutationTypes';
import { UPDATE_QUESTION } from './store/questions/actions/actionTypes';
import {
  UPDATE_JOB,
  UPDATE_REPAIR_ASSESSMENT_ROW,
} from './store/jobs/actions/actionTypes';

import {
  SEND_WEBSOCKET_MESSAGE,
  UPDATE_WEB_SOCKET_CONNECTION_ID,
  UPDATE_WEB_SOCKET_STATUS,
  WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION,
} from './store/websocket/actions/actionTypes';

export default {
  name: 'App',
  data() {
    return {
      parsedUser: {},
      tools: ['admin', 'editor', 'publisher', 'inspector', 'report'],
      sessionStorageStatus: false,
      keyToRefresh: 0,
      showRefreshMessage: false,
      notificationVisible: false,
      notificationMessage: null,
      notificationType: null,
      notificationAutoClose: false,
      notificationAutoCloseDuration: 0,
      dialogConflicts: [],
      conflictDialogVisible: false,
      reassignProgressClearTimer: null,
    };
  },
  components: {
    Header,
    ConflictDialog,
    ProgressTaskBar,
  },
  mixins: [OfflineMixin, ConnectivitySyncMixin],
  async created() {
    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'authorized',
      callback: this.wsAuthorized,
    });

    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'unauthorized',
      callback: this.wsUnauthorized,
    });

    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'authorizationRequest',
      callback: this.wsAuthorizationRequest,
    });

    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'jobCreationProgress',
      callback: this.handleJobCreationProgress,
    });

    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'taskCreationProgress',
      callback: this.handleTaskCreationProgress,
    });

    this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
      action: 'reassignOwnershipProgress',
      callback: this.handleReassignOwnershipProgress,
    });
  },
  computed: {
    progressBarValue() {
      return this.$store.state.progressBarValue;
    },
    progressBarMessage() {
      return this.$store.state.progressBarMessage;
    },
    conflictManagerConflicts() {
      return this.$store.getters[GET_ALL_CONFLICTS];
    },
    user() {
      return this.$store.state.currentUser;
    },
    userWithoutSite() {
      return this.$store.state.userWithoutSite;
    },
    successNotificationVisible() {
      return (
        this.notificationVisible === true &&
        this.notificationType === 'is-success'
      );
    },
    warningNotificationVisible() {
      return (
        this.notificationVisible === true &&
        this.notificationType === 'is-warning'
      );
    },
    errorNotificationVisible() {
      return (
        this.notificationVisible === true &&
        this.notificationType === 'is-danger'
      );
    },
    announcementBannerMessage() {
      return this.$store.state.announcementBannerMessage;
    },
    announcementBannerVisible() {
      const now = new Date();
      const dismissedUntil = new Date(
        localStorage.getItem('bannerDismissedUntil')
      );

      return (
        this.$store.state.announcementBannerVisible &&
        this.$store.state.announcementBannerMessage &&
        this.$store.state.announcementBannerStartDate <= now &&
        this.$store.state.announcementBannerEndDate > now &&
        (isNaN(dismissedUntil.getTime()) || dismissedUntil <= now)
      );
    },
  },
  watch: {
    async computedIsOffline(offline) {
      this.$buefy.toast.open({
        duration: 3000,
        message: offline
          ? 'Connection lost. Switched to offline mode.'
          : // eslint-disable-next-line quotes
            "Connection restored. You're back online",
        type: 'is-success',
        position: 'is-bottom',
      });
    },

    conflictManagerConflicts(newConflicts) {
      if (Array.isArray(newConflicts) && newConflicts.length !== 0) {
        const uniqueConflictsMap = new Map();

        newConflicts.forEach((conflict) => {
          const id = conflict?.localEntity?._id || conflict?.localEntity?.id;
          if (id) {
            uniqueConflictsMap.set(id, conflict);
          }
        });

        this.dialogConflicts = Array.from(uniqueConflictsMap.values());
        this.conflictDialogVisible = true;
      }
    },
  },
  async beforeUpdate() {
    // Try to get the announcement banner every update
    if (this.user && !this.user.isPendingApproval && !this.user.isRejected) {
      this.$store.dispatch('getAnnouncementBanner');
    }
  },
  async beforeMount() {
    await this.initializeConnectivityRecovery();

    ['search', 'inspector_page', 'publisher_page', 'report_page'].forEach(
      (sessionStorageName) => {
        if (sessionStorage.getItem(sessionStorageName)) {
          sessionStorage.removeItem(sessionStorageName);
        }
      }
    );
  },
  async mounted() {
    // transfers sessionStorage from one tab to another
    const myself = this;
    const sessionStorage_transfer = function (event) {
      if (!event) {
        event = window.event;
      } // ie suq
      if (!event.newValue) return; // do nothing if no value to work with
      if (event.key === 'getSessionStorage') {
        // another tab asked for the sessionStorage -> send it
        localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
        // the other tab should now have it, so we're done with it.
        localStorage.removeItem('sessionStorage'); // <- could do short timeout as well.
      } else if (event.key === 'sessionStorage' && !sessionStorage.length) {
        // another tab sent data <- get it
        const data = JSON.parse(event.newValue);
        Object.keys(data).forEach((key) => {
          sessionStorage.setItem(key, data[key]);
        });
        myself.sessionStorageStatus = true;
      }
    };

    try {
      const isOnline = await onlineService.canConnectRealtime();
      const token = await acquireToken();

      if (!token) {
        console.log('Waiting for login...');
      } else if (isOnline) {
        console.log('Registering websocket connection');
        setTimeout(async () => {
          this.$store.dispatch(SEND_WEBSOCKET_MESSAGE, {
            action: 'registerToken',
            data: token,
          });
        }, 500);
      }
    } catch (error) {
      console.error(error);
    }

    window.addEventListener(
      'refreshPolymer',
      () => {
        myself.keyToRefresh += 1;
        myself.$forceUpdate();
      },
      false
    );

    // TODO SWITCH THIS FOR ERROR NOTIFICATION
    // Error message
    // Your session is expired and your page will be refreshed.
    window.addEventListener('showRefreshMessage', () => {
      this.notificationType = 'is-danger';
      this.notificationMessage =
        'Your session is expired and your page will be refreshed.';
      this.notificationAutoClose = false;
      this.notificationVisible = true;
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    });

    // listen for changes to localStorage
    if (window.addEventListener) {
      window.addEventListener('storage', sessionStorage_transfer, false);
    } else {
      window.attachEvent('onstorage', sessionStorage_transfer);
      // window.attachEvent('onstorage', sessionStorage_transfer);
    }

    // Ask other tabs for session storage (this is ONLY to trigger event)
    if (!sessionStorage.length) {
      localStorage.setItem('getSessionStorage', 'foobar');
      localStorage.removeItem('getSessionStorage', 'foobar');
    } else {
      this.sessionStorageStatus = true;
    }

    // Notification message handlers
    // Info messages only override themselfs
    this.$store.watch(
      () => {
        return this.$store.state.infoMessage;
      },
      (infoMessage) => {
        if (
          infoMessage &&
          !this.successNotificationVisible &&
          !this.warningNotificationVisible &&
          !this.errorNotificationVisible
        ) {
          this.notificationType = 'is-info';
          this.notificationMessage = infoMessage;
          this.notificationAutoClose = true;
          this.notificationAutoCloseDuration = 3000;
          this.notificationVisible = true;
        }
      }
    );
    // Success messages override info, themselfs but not warning and error messages
    this.$store.watch(
      () => {
        return this.$store.state.successMessage;
      },
      (successMessage) => {
        if (
          successMessage &&
          !this.warningNotificationVisible &&
          !this.errorNotificationVisible
        ) {
          this.notificationType = 'is-success';
          this.notificationMessage = successMessage;
          this.notificationAutoClose = true;
          this.notificationAutoCloseDuration = 3000;
          this.notificationVisible = true;
        }
      }
    );
    // Warning messages override info, success and themselfs but not error messages
    this.$store.watch(
      () => {
        return this.$store.state.warningMessage;
      },
      (warningMessage) => {
        if (warningMessage && !this.errorNotificationVisible) {
          this.notificationType = 'is-warning';
          this.notificationMessage = warningMessage;
          this.notificationAutoClose = true;
          this.notificationAutoCloseDuration = 10000;
          this.notificationVisible = true;
        }
      }
    );
    // Error messages overrides every message type
    this.$store.watch(
      () => {
        return this.$store.state.errorMessage;
      },
      (errorMessage) => {
        if (errorMessage) {
          this.notificationType = 'is-danger';
          this.notificationMessage = errorMessage;
          this.notificationAutoClose = false;
          this.notificationVisible = true;
        }
      }
    );
  },
  beforeDestroy() {
    this.teardownConnectivityRecovery();

    if (this.reassignProgressClearTimer) {
      clearTimeout(this.reassignProgressClearTimer);
      this.reassignProgressClearTimer = null;
    }
  },

  methods: {
    dismissBanner() {
      const endDate = this.$store.state.announcementBannerEndDate;
      localStorage.setItem('bannerDismissedUntil', endDate);
      this.$store.commit('setAnnouncementBannerVisible', false);
    },
    async onResolved(resolvedEntities) {
      const job = this.$store.state.JobsModule.currentJob;

      const tasks = resolvedEntities.map(({ resolved, forceFields }) => {
        const key = resolved?.conflictDetails?.conflictKey;

        if (key === 'updateRepairAssessmentRow') {
          return this.$store.dispatch(UPDATE_REPAIR_ASSESSMENT_ROW, {
            job,
            row: resolved,
            tool: this.tool,
            forceFields,
          });
        }
        if (key === 'updateQuestion') {
          return this.$store.dispatch(UPDATE_QUESTION, resolved);
        }
        if (key === 'updateJob') {
          return this.$store.dispatch(UPDATE_JOB, resolved);
        }
        if (key === 'updateBubble') {
          this.$root.$emit('updateBubble', resolved);
          return null; // sync side-effect, not a promise
        }

        console.error(`Unknown conflictKey: ${key}`);
        return null;
      });

      await Promise.all(tasks.filter(Boolean));
      this.onCancel();
    },
    onCancel() {
      this.$store.commit(CONFLICTS_CLEAR_ALL_CONFLICTS);
      this.conflictDialogVisible = false;
    },
    // eslint-disable-next-line no-unused-vars
    async wsAuthorizationRequest(data) {
      console.log('Received websocket authorization request...');

      try {
        const isOnline = await onlineService.canConnectRealtime();
        if (!isOnline) {
          return;
        }

        const token = await acquireToken();

        if (!token) {
          console.log('Waiting for login...');
        } else {
          console.log('Sending register token due to authorization request');
          setTimeout(async () => {
            this.$store.dispatch(SEND_WEBSOCKET_MESSAGE, {
              action: 'registerToken',
              data: token,
            });
          }, 0);
        }
      } catch (error) {
        console.error(error);
      }
    },
    wsAuthorized(data) {
      console.log('Received websocket authorization: ', data);
      this.$store.dispatch(UPDATE_WEB_SOCKET_CONNECTION_ID, data);
      this.$store.dispatch(UPDATE_WEB_SOCKET_STATUS, 'Connected');

      const currentJob =
        this.$store.state?.JobsModule?.currentJob ||
        this.$store.state?.currentJob;
      if (currentJob && currentJob._id) {
        this.$store.dispatch(SEND_WEBSOCKET_MESSAGE, {
          action: 'jobJoin',
          data: { _id: currentJob._id },
        });
      }
    },
    wsUnauthorized(data) {
      this.$store.dispatch(
        UPDATE_WEB_SOCKET_STATUS,
        'Disconnected (Unauthorized)'
      );
      console.warn(`Websocket: ${data}`);
    },
    hideNotification() {
      this.notificationVisible = false;
      this.notificationMessage = null;
      this.$store.dispatch('clearAllMessages');
    },
    hideAnnouncementBanner() {
      this.$store.dispatch('hideAnnouncementBanner');
      this.dismissBanner();
    },
    handleJobCreationProgress(data) {
      if (data && typeof data.progress === 'number') {
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'ws-job-creation',
          value: data.progress,
          message: data.message || 'Creating product',
        });
        if (
          data.status === 'completed' ||
          data.status === 'failed' ||
          data.progress >= 100
        ) {
          setTimeout(() => {
            this.$store.commit('SET_PROGRESS_BAR', {
              key: 'ws-job-creation',
              value: null,
            });
          }, 600);
        }
      }
    },
    handleTaskCreationProgress(data) {
      if (data && typeof data.progress === 'number') {
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'ws-task-creation',
          value: data.progress,
          message: data.message || 'Creating task',
        });
        if (
          data.status === 'completed' ||
          data.status === 'failed' ||
          data.progress >= 100
        ) {
          setTimeout(() => {
            this.$store.commit('SET_PROGRESS_BAR', {
              key: 'ws-task-creation',
              value: null,
            });
          }, 600);
        }
      }
    },
    handleReassignOwnershipProgress(data) {
      if (!data || typeof data.progress !== 'number') {
        return;
      }

      if (this.reassignProgressClearTimer) {
        clearTimeout(this.reassignProgressClearTimer);
        this.reassignProgressClearTimer = null;
      }

      this.$store.commit('SET_PROGRESS_BAR', {
        key: 'ws-reassign',
        value: data.progress,
        message: data.message || 'Reassigning ownership',
      });

      if (data.status === 'completed' || data.status === 'failed') {
        this.reassignProgressClearTimer = setTimeout(() => {
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'ws-reassign',
            value: null,
            message: '',
          });
          this.reassignProgressClearTimer = null;
        }, 600);
      }
    },
  },
};
</script>

<style>
@font-face {
  font-family: 'Siemens Sans';
  src: url(/fonts/siemenswebfonts/sisan06.ttf) format('truetype');
}

@font-face {
  font-family: 'Siemens Roman';
  src: url(/fonts/siemenswebfonts/sisan03.ttf) format('truetype');
}

.notification-area {
  position: absolute;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  top: 103px;
}

.notification-area div.media {
  align-items: center;
}

.notification.dij-announcement-banner {
  padding-top: 10px;
  padding-bottom: 10px;
  margin: 0;
  background-color: rgb(255, 224, 138);
}
</style>
